import {Button} from '@wandb/components';
import {TooltipProps} from '@wandb/components/dist/components/Tooltip';
import React, {useCallback} from 'react';

import {PanelQuickAddEvent} from '../../../services/analytics/panelQuickAddEvents';
import {useDispatch} from '../../../state/hooks';
import * as PanelBankConfigActions from '../../../state/views/panelBankConfig/actions';
import * as PanelBankSectionConfigActions from '../../../state/views/panelBankSectionConfig/actions';
import {getDefaultPanelConfig} from '../../../util/panelbank';
import {maybePluralize} from '../../../util/uihelpers';
import {useWorkspaceRefsContext} from '../../Workspace/WorkspaceRefsContext';
import {useWorkspaceSettingsContext} from '../Settings/WorkspaceSettingsContextProvider';
import {useWorkspaceDrawerContext} from '../WorkspaceDrawerContextProvider';
import {QuickAddKeys} from './PanelsQuickAddContext';

type AddBulkButtonProps = {
  searchedKeys: QuickAddKeys;
  keysWithNoPanels: QuickAddKeys;
  trackQuickAddBound: (event: Omit<PanelQuickAddEvent, 'scope'>) => void;
  onBulkAdd?: () => void;
};

export const AddBulkButton = ({
  searchedKeys,
  keysWithNoPanels,
  trackQuickAddBound,
  onBulkAdd,
}: AddBulkButtonProps) => {
  const numKeysInList = searchedKeys.length;
  const numKeysToAdd = keysWithNoPanels.length;
  const isSomeKeysAdded = numKeysInList > numKeysToAdd;

  const {panelBankConfigRef} = useWorkspaceRefsContext();
  const {targetSectionRef} = useWorkspaceDrawerContext();
  const {
    workspaceLayout: {sortAlphabetically},
  } = useWorkspaceSettingsContext();

  const dispatch = useDispatch();
  const onAddBulkClick = useCallback(() => {
    trackQuickAddBound({action: 'Add bulk clicked'});
    if (targetSectionRef) {
      const panelConfigs = keysWithNoPanels.map(([key, spec]) =>
        getDefaultPanelConfig(key, spec)
      );
      dispatch(
        PanelBankSectionConfigActions.addPanelsByConfig(
          targetSectionRef,
          panelConfigs,
          sortAlphabetically
        )
      );
    } else {
      dispatch(
        PanelBankConfigActions.addPanelsBySpec(
          panelBankConfigRef,
          keysWithNoPanels.map(([key, spec]) => ({key, spec})),
          sortAlphabetically
        )
      );
    }
    onBulkAdd?.();
  }, [
    dispatch,
    keysWithNoPanels,
    onBulkAdd,
    panelBankConfigRef,
    sortAlphabetically,
    targetSectionRef,
    trackQuickAddBound,
  ]);

  let tooltip: TooltipProps | undefined;
  if (isSomeKeysAdded) {
    tooltip = {
      children: "Generate panels for keys that haven't been added yet.",
    };
  }

  return (
    <Button fullWidth onPress={onAddBulkClick} tooltip={tooltip}>
      {`Create ${maybePluralize(numKeysToAdd, 'panel', 's', true)}`}
    </Button>
  );
};
